import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { CreditCardDrawerBaseComponent } from '@shared/components/page-layout/drawers/credit-card-drawer-base.component';
import braintree from 'braintree-web';

@Component({
    selector: 'app-braintree-credit-card-drawer',
    templateUrl: './braintree-credit-card-drawer.component.html',
    styleUrls: ['./braintree-credit-card-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon, FormsModule, ReactiveFormsModule, MatCheckbox, MatFormField, MatLabel, MatSelect, MatOption, MatInput, MatButton]
})
export class BraintreeCreditCardDrawerComponent extends CreditCardDrawerBaseComponent {
    cardType: string;
    hostedFields: braintree.HostedFields;

    protected setupGatewayFormControls(isEditMode: boolean): void {
        const validators = isEditMode ? [] : [Validators.required];
        this.creditCardValidationForm.addControl('number', this.formBuilder.control('', validators));
        this.creditCardValidationForm.addControl('expirationMonth', this.formBuilder.control('', validators));
        this.creditCardValidationForm.addControl('expirationYear', this.formBuilder.control('', validators));
        this.creditCardValidationForm.addControl('cvv', this.formBuilder.control('', validators));
        this.creditCardValidationForm.addControl('postalCode', this.formBuilder.control('', validators));
    }

    protected initializeGateway(token: string): void {
        braintree.client.create({
            authorization: token
        }, this.clientInstance.bind(this) as typeof this.clientInstance);
    }

    protected submitNewCard(): void {
        this.hostedFields?.tokenize(this.onTokenize.bind(this) as typeof this.onTokenize);
    }

    clientInstance(err: braintree.BraintreeError, clientInstance: braintree.Client) {
        if (err) {
            this.alertService.errorToast('Error', 'Error initializing payment form. Please try again later.');
            return;
        }

        braintree.hostedFields.create({
            client: clientInstance,
            styles: {
                'input': {
                    'font-size': '14px',
                    'font-family': 'Rambla, helvetica, tahoma, calibri, sans-serif',
                    'color': '#3a3a3a'
                },
                ':focus': {
                    'color': 'black'
                },
                'input.invalid': {
                    'color': 'red'
                },
                'input.valid': {
                    'color': 'green'
                }
            },
            fields: {
                number: {
                    selector: '#cardNumber',
                    placeholder: '#### #### #### ####',
                    type: 'number'
                },
                cvv: {
                    selector: '#cardCVV',
                    placeholder: '123'
                },
                expirationMonth: {
                    selector: '#expirationMonth',
                    placeholder: 'MM'
                },
                expirationYear: {
                    selector: '#expirationYear',
                    placeholder: 'YY'
                },
                postalCode: {
                    selector: '#billingZipCode',
                    placeholder: '90210'
                }
            }
        }, this.onHostedFields.bind(this) as typeof this.onHostedFields);
    }

    onHostedFields(err: braintree.BraintreeError, hostedFieldsInstanceMain: braintree.HostedFields) {
        if (err) {
            this.alertService.errorToast('Error', 'Error getting hosted fields. Please try again later.');
            return;
        }

        if (hostedFieldsInstanceMain !== undefined && hostedFieldsInstanceMain !== null) {
            hostedFieldsInstanceMain.on('validityChange', this.onValidityChange.bind(this) as typeof this.onValidityChange);
            hostedFieldsInstanceMain.on('cardTypeChange', this.onCardTypeChange.bind(this) as typeof this.onCardTypeChange);
            this.hostedFields = hostedFieldsInstanceMain;
            this.busy = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    onCardTypeChange(event: braintree.HostedFieldsEvent) {
        if (event.cards.length === 1
            && event.cards[0] !== undefined
            && event.cards[0] !== null
            && event.cards[0].niceType !== '') {
            this.cardType = event.cards[0].niceType;
        } else {
            this.cardType = null;
        }

        this.changeDetectorRef.detectChanges();
    }

    onValidityChange(event: braintree.HostedFieldsEvent) {

        (Object.keys(event.fields) as (keyof braintree.HostedFieldsEvent['fields'])[]).forEach(field => {

            this.creditCardValidationForm.controls[field].markAsDirty();

            if (event.fields[field].isValid === true) {

                this.creditCardValidationForm.controls[field].setValue('VALUE');
            } else {
                this.creditCardValidationForm.controls[field].setValue(null);
            }
        });

        this.changeDetectorRef.detectChanges();
    }

    onTokenize(err: braintree.BraintreeError, payload: braintree.HostedFieldsTokenizePayload) {
        if (err) {
            this.alertService.errorToast('Error', 'Error validating credit card details. Please check your information and try again.');
            return;
        }

        this.persistNewCard({
            externalCardIdNonce: payload.nonce,
            last4DigitsCC: payload.details.lastFour,
            cardType: payload.details.cardType
        });
    }
}
